import type { RuleFiringBatch } from '@tunasong/schemas'
import type { FC } from 'react'
import { formatLastFired } from './automation-summary.js'

export interface RuleHistoryProps {
  batches: RuleFiringBatch[]
  /** True while the firing-batch data behind `batches` has not loaded yet. An empty `batches`
   * is indistinguishable from "never fired" otherwise, so a freshly selected rule would flash
   * "Hasn't run yet" during the load -- render a neutral loading state instead of asserting that. */
  loading?: boolean
  /** Injected so relative times are testable without mocking the clock. */
  now?: Date
  className?: string
}

const describeBatch = (batch: RuleFiringBatch): string => {
  const count = batch.appliedActions.length
  if (count === 0) {
    return 'Ran, changed nothing'
  }
  return `Changed ${count} item${count === 1 ? '' : 's'}`
}

export const RuleHistory: FC<RuleHistoryProps> = props => {
  const { batches, loading = false, now = new Date(), className } = props

  // firedAt is fixed-format ISO-8601 (new Date().toISOString()), so code-point order equals chronological order.
  // Plain comparison avoids unnecessary locale dependency.
  const newestFirst = [...batches].sort((a, b) => (a.firedAt < b.firedAt ? 1 : -1))

  return (
    <div className={className}>
      {loading ? (
        <p>Loading…</p>
      ) : batches.length === 0 ? (
        <p>Hasn&apos;t run yet</p>
      ) : (
        <ul>
          {newestFirst.map(batch => (
            <li key={batch.batchId}>
              {describeBatch(batch)} · {formatLastFired(batch.firedAt, now)}
            </li>
          ))}
        </ul>
      )}
      {/* Reachable in the empty case too: firing batches carry a 90-day TTL, so a rule that
       * last fired 100 days ago lands here with no batches left -- the note is what explains
       * that its history simply aged out, rather than the rule never having run at all. */}
      <p>History and undo are kept for 90 days.</p>
    </div>
  )
}
